import { useState } from "react";
import { ArrowLeft, Info, Expand } from "lucide-react";
import { useLang } from "@/i18n";
import { IMG } from "@/data/media";
import { FadeUp } from "@/components/motion";
import { Lightbox } from "@/components/Lightbox";
import { L } from "@/components/L";

export const BackLink = () => {
  const { t } = useLang();
  return <L to="projects" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--green-deep)] link-u" data-testid="back-to-projects"><ArrowLeft size={15} /> {t.common.backToProjects}</L>;
};

export const Objectives = ({ items }) => {
  const { t } = useLang();
  return (
    <section className="py-20 sm:py-28 bg-white" data-testid="project-objectives">
      <div className="wrap grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-4"><span className="eyebrow">{t.projects.objectivesTitle}</span></div>
        <ol className="lg:col-span-8 space-y-0">
          {items.map((o, i) => (
            <FadeUp as="li" key={i} delay={i * 0.06} className="grid grid-cols-[3.5rem_1fr] gap-4 py-7 border-t border-[var(--line)] last:border-b">
              <span className="font-display italic text-3xl text-[var(--green-deep)]">0{i + 1}</span>
              <p className="text-xl sm:text-2xl font-display leading-snug">{o}</p>
            </FadeUp>
          ))}
        </ol>
      </div>
    </section>
  );
};

export const StatusNote = () => {
  const { t } = useLang();
  return (
    <div className="wrap">
      <p className="flex gap-3 items-start text-sm text-[var(--ink-soft)] bg-[var(--blue-pale)] rounded-2xl p-5 max-w-3xl" data-testid="project-status-note">
        <Info size={18} className="shrink-0 mt-0.5 text-[var(--green-deep)]" /> {t.common.statusNote}
      </p>
    </div>
  );
};

export const ProjectGallery = ({ keys }) => {
  const { t } = useLang();
  const [idx, setIdx] = useState(null);
  const items = keys.map((k) => ({ src: IMG[k], caption: t.gallery.captions[k] }));
  return (
    <section className="py-20 sm:py-28" data-testid="project-gallery">
      <div className="wrap">
        <h2 className="text-3xl sm:text-4xl">{t.common.projectGallery}</h2>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {items.map((it, i) => (
            <FadeUp key={it.src} delay={i * 0.05}>
              <button onClick={() => setIdx(i)} aria-label={`${t.gallery.open}: ${it.caption}`} className="group relative block w-full aspect-square overflow-hidden rounded-[1.5rem] img-zoom bg-white shadow-[0_0_0_1px_var(--line)]" data-testid={`project-gallery-item-${i}`}>
                <img src={it.src} alt={it.caption} loading="lazy" className="photo w-full h-full object-cover" />
                <span className="absolute right-3 bottom-3 h-9 w-9 grid place-items-center rounded-full bg-white/90 opacity-0 group-hover:opacity-100 transition-opacity"><Expand size={15} /></span>
              </button>
              <p className="mt-2 text-sm text-[var(--ink-soft)]">{it.caption}</p>
            </FadeUp>
          ))}
        </div>
      </div>
      <Lightbox items={items} index={idx} onClose={() => setIdx(null)} onChange={setIdx} />
    </section>
  );
};
